import * as React from 'react';
import './StressBarometer.css';
import { inject, observer } from 'mobx-react';
import { AppStore } from '../../../stores';
import { IStressBarometerArea } from '../../../interfaces/IStressBarometer';

export interface IProps {
  appStore?: AppStore;
  showImportance?: boolean;
  showValue?: boolean;
  showStress?: boolean;
}

export interface IState {
  showImportance?: boolean;
  showValue?: boolean;
  showStress?: boolean;
}

@inject('appStore')
@observer
export default class StressBarometer extends React.Component<IProps, IState> {

  private backgroundColor = '#555';
  private importanceColor = '#7a12d4';
  private valueColor = '#3ab5e6';
  private stressColor = '#e63a3a';

  constructor(props) {
    super(props);
    this.state = {
      showImportance: true,
      showValue: true,
      showStress: true
    }
  }

  createRingSegments(outerRadius: number, innerRadius: number, answerValue) {
    const centerX = 300; // Updated center position for larger SVG
    const centerY = 300; // Updated center position for larger SVG
    const segments = [];

    for (let i = 0; i < 8; i++) {
      const startAngle = (i * 45) - 90;
      const endAngle = ((i + 1) * 45) - 90;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      // Define colors based on the result value for this segment
      let segmentColor;
      if (this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance < 5) {
        segmentColor = this.stressColor;
      } else if (this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance >= 5 && this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance <= 7) {
        segmentColor = this.stressColor;
      } else if (this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance > 7) {
        segmentColor = this.stressColor;
      }
      const showStress = this.props.showStress !== undefined ? this.props.showStress : this.state.showStress;
      segmentColor = showStress ? segmentColor : this.backgroundColor;

      let path;

      if (innerRadius === 0) {
        const x1 = centerX + outerRadius * Math.cos(startAngleRad);
        const y1 = centerY + outerRadius * Math.sin(startAngleRad);
        const x2 = centerX + outerRadius * Math.cos(endAngleRad);
        const y2 = centerY + outerRadius * Math.sin(endAngleRad);

        path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 0 1 ${x2} ${y2} Z`;
      } else {
        const x1 = centerX + outerRadius * Math.cos(startAngleRad);
        const y1 = centerY + outerRadius * Math.sin(startAngleRad);
        const x2 = centerX + outerRadius * Math.cos(endAngleRad);
        const y2 = centerY + outerRadius * Math.sin(endAngleRad);

        const x3 = centerX + innerRadius * Math.cos(endAngleRad);
        const y3 = centerY + innerRadius * Math.sin(endAngleRad);
        const x4 = centerX + innerRadius * Math.cos(startAngleRad);
        const y4 = centerY + innerRadius * Math.sin(startAngleRad);

        path = `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 0 0 ${x4} ${y4} Z`;
      }
      const lowValue = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance < this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value ? this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance : this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value;
      const highValue = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance > this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value ? this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance : this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value;
      segments.push(
        <path
          key={`ring-${outerRadius}-segment-${i}`}
          d={path}
          fill={answerValue <= highValue && answerValue > lowValue ? segmentColor : '#555'}
          stroke="#252525"
          strokeWidth="1"
        />
      );

      // Add text labels only for the outermost ring
      if (outerRadius === 225) {
        const midAngle = (startAngle + endAngle) / 2;
        const midAngleRad = (midAngle * Math.PI) / 180;
        const labelRadius = outerRadius + 60; // Increased margin from 30 to 60px

        const textX = centerX + labelRadius * Math.cos(midAngleRad);
        const textY = centerY + labelRadius * Math.sin(midAngleRad);

        segments.push(
          <text
            key={`label-${i}`}
            x={textX}
            y={textY}
            fill="#ffffff"
            fontSize="12"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {this.props.appStore!.stressBarometerToFillIn?.areas?.[i].title}
          </text>
        );
      }
    }

    return segments;
  }

  createImportanceLines() {
    const centerX = 300;
    const centerY = 300;
    const lines = [];
    const showImportance = this.props.showImportance !== undefined ? this.props.showImportance : this.state.showImportance;
    const showValue = this.props.showValue !== undefined ? this.props.showValue : this.state.showValue;

    for (let i = 0; i < 8; i++) {
      const importance = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance;
      const value = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value;

      // Skip if both lines are enabled and values are equal (will be handled by combined line)
      if (showValue && showImportance && importance === value) {
        continue;
      }

      const importanceRadius = (importance / 10) * 225; // Scale importance (1-10) to radius (0-225)

      const startAngle = (i * 45) - 90;
      const endAngle = ((i + 1) * 45) - 90;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      // Create points for the importance line at the specified radius
      const x1 = centerX + importanceRadius * Math.cos(startAngleRad);
      const y1 = centerY + importanceRadius * Math.sin(startAngleRad);
      const x2 = centerX + importanceRadius * Math.cos(endAngleRad);
      const y2 = centerY + importanceRadius * Math.sin(endAngleRad);

      // Create arc path for the importance line
      const largeArcFlag = 0; // Since each segment is 45 degrees, never more than 180
      const path = `M ${x1} ${y1} A ${importanceRadius} ${importanceRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

      lines.push(
        <path
          key={`importance-line-${i}`}
          d={path}
          fill="none"
          stroke={this.importanceColor}
          strokeWidth="3"
          strokeLinecap="round"
        />
      );
    }

    return lines;
  }

  createValueLines() {
    const centerX = 300;
    const centerY = 300;
    const lines = [];
    const showImportance = this.props.showImportance !== undefined ? this.props.showImportance : this.state.showImportance;
    const showValue = this.props.showValue !== undefined ? this.props.showValue : this.state.showValue;

    for (let i = 0; i < 8; i++) {
      const value = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value;
      const importance = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance;

      // Skip if both lines are enabled and values are equal (will be handled by combined line)
      if (showValue && showImportance && importance === value) {
        continue;
      }

      const valueRadius = (value / 10) * 225; // Scale value (1-10) to radius (0-225)

      const startAngle = (i * 45) - 90;
      const endAngle = ((i + 1) * 45) - 90;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      // Create points for the value line at the specified radius
      const x1 = centerX + valueRadius * Math.cos(startAngleRad);
      const y1 = centerY + valueRadius * Math.sin(startAngleRad);
      const x2 = centerX + valueRadius * Math.cos(endAngleRad);
      const y2 = centerY + valueRadius * Math.sin(endAngleRad);

      // Create arc path for the value line
      const largeArcFlag = 0; // Since each segment is 45 degrees, never more than 180
      const path = `M ${x1} ${y1} A ${valueRadius} ${valueRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

      lines.push(
        <path
          key={`value-line-${i}`}
          d={path}
          fill="none"
          stroke={this.valueColor}
          strokeWidth="3"
          strokeLinecap="round"
        />
      );
    }

    return lines;
  }

  createCombinedLines() {
    const centerX = 300;
    const centerY = 300;
    const lines = [];

    for (let i = 0; i < 8; i++) {
      const importance = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].importance;
      const value = this.props.appStore!.stressBarometerToFillIn?.areas?.[i].value;

      // Only create combined line if importance equals value
      if (importance === value) {
        const radius = (importance / 10) * 225; // Scale to radius (0-225)

        const startAngle = (i * 45) - 90;
        const endAngle = ((i + 1) * 45) - 90;

        const startAngleRad = (startAngle * Math.PI) / 180;
        const endAngleRad = (endAngle * Math.PI) / 180;

        // Create points for the combined line at the specified radius
        const x1 = centerX + radius * Math.cos(startAngleRad);
        const y1 = centerY + radius * Math.sin(startAngleRad);
        const x2 = centerX + radius * Math.cos(endAngleRad);
        const y2 = centerY + radius * Math.sin(endAngleRad);

        // Create arc path for the combined line
        const largeArcFlag = 0; // Since each segment is 45 degrees, never more than 180
        const path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

        // Create pattern for alternating blue-green stripes
        lines.push(
          <defs key={`defs-${i}`}>
            <pattern id={`stripePattern-${i}`} patternUnits="userSpaceOnUse" width="12" height="3" patternTransform="rotate(0)">
              <rect width="6" height="3" fill={this.importanceColor} />
              <rect x="6" width="6" height="3" fill={this.valueColor} />
            </pattern>
          </defs>
        );

        lines.push(
          <path
            key={`combined-line-${i}`}
            d={path}
            fill="none"
            stroke={`url(#stripePattern-${i})`}
            strokeWidth="3"
            strokeLinecap="round"
          />
        );
      }
    }

    return lines;
  }

  toggleImportance = () => {
    this.setState({ showImportance: !this.state.showImportance });
  }

  toggleValue = () => {
    this.setState({ showValue: !this.state.showValue });
  }

  toggleStress = () => {
    this.setState({ showStress: !this.state.showStress });
  }

  render() {
    return (
      <div className='stressBarometerContent'>
        <section id="container" className="stressBarometerSvgContainer">
          <svg
            width="100%"
            height={this.props.appStore!.isMobile ? screen.availWidth - 40 : 680} 
            viewBox="-40 -20 680 640"
            preserveAspectRatio="xMidYMid meet"
            className="target-svg"
            style={{
              overflow: "visible",
              display: "block"
            }}
          >
            {/* Ring 10 - Outermost */}
            {this.createRingSegments(225, 202.5, 10)}

            {/* Ring 9 */}
            {this.createRingSegments(202.5, 180, 9)}

            {/* Ring 8 */}
            {this.createRingSegments(180, 157.5, 8)}

            {/* Ring 7 */}
            {this.createRingSegments(157.5, 135, 7)}

            {/* Ring 6 */}
            {this.createRingSegments(135, 112.5, 6)}

            {/* Ring 5 */}
            {this.createRingSegments(112.5, 90, 5)}

            {/* Ring 4 */}
            {this.createRingSegments(90, 67.5, 4)}

            {/* Ring 3 */}
            {this.createRingSegments(67.5, 45, 3)}

            {/* Ring 2 */}
            {this.createRingSegments(45, 22.5, 2)}

            {/* Ring 1 - Center with segments */}
            {this.createRingSegments(22.5, 0, 1)}

            {/* Importance lines */}
            {(this.props.showImportance !== undefined ? this.props.showImportance : this.state.showImportance) && this.createImportanceLines()}

            {/* Value lines */}
            {(this.props.showValue !== undefined ? this.props.showValue : this.state.showValue) && this.createValueLines()}

            {/* Combined lines when importance equals value */}
            {(this.props.showImportance !== undefined ? this.props.showImportance : this.state.showImportance) && (this.props.showValue !== undefined ? this.props.showValue : this.state.showValue) && this.createCombinedLines()}
          </svg>
        </section>
      </div>
    );
  }
}